/**
 * Pure semantic projection of the canonical RoutePlotter project.
 *
 * The canvas remains the visual renderer. This module creates a bounded,
 * DOM-free view model for the keyboard/non-visual scene outline. It never
 * mutates models and never copies image bytes or other private metadata.
 */

import { buildRouteNumbering } from './waypointNaming.js';
import { getGraphEntryShares } from './graphRouting.js';
import { entryWeightValue } from './entryWeight.js';
import { groupsUnavailableReason, groupsActive, groupChoiceRole } from './groupSettings.js';
import { CROWD_GROUPS } from '../config/constants.js';
import { waitingLayerReason, waitingUnavailableReason, waitingActive } from './waitingSettings.js';
import { shiftDecimal } from './uiReadouts.js';

const round = (value, places = 3) => {
  const factor = 10 ** places;
  return Math.round(Number(value) * factor) / factor;
};

// Keep authoring values lossless. Headings and position fields show the
// rounded companion labels (UI-06 W-02); a field carries the canonical value
// beside it, which a field never edited submits, so nothing is lost. One the
// author typed in, even back to what it showed, submits what it holds
// (SceneOutlineController). Other number inputs receive the full JS
// round-trip value.
const percent = value => Number(value) * 100;
const percentLabel = value => round(Number(value) * 100, 2);

/**
 * Build an injective semantic key from a typed tuple. Encoding every part
 * keeps the colon separator structural: IDs containing colons or percent-like
 * text cannot impersonate a different tuple or one of the controller suffixes.
 * Existing generated IDs remain human-readable. A persisted lone UTF-16
 * surrogate is escaped with a reserved `%uXXXX` form: ordinary percent signs
 * become `%25`, so malformed-but-accepted legacy IDs remain distinct without
 * making `encodeURIComponent` throw and disable the whole outline.
 *
 * @param {string} kind
 * @param {...(string|number)} parts
 * @returns {string}
 */
export function sceneOutlineKey(kind, ...parts) {
  return [kind, ...parts].map(encodeSceneKeyPart).join(':');
}

export function encodeSceneKeyPart(part) {
  const value = String(part);
  let encoded = '';
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    const next = value.charCodeAt(index + 1);
    if (code >= 0xD800 && code <= 0xDBFF && next >= 0xDC00 && next <= 0xDFFF) {
      encoded += encodeURIComponent(value.slice(index, index + 2));
      index += 1;
    } else if (code >= 0xD800 && code <= 0xDFFF) {
      encoded += `%u${code.toString(16).toUpperCase().padStart(4, '0')}`;
    } else {
      encoded += encodeURIComponent(value[index]);
    }
  }
  return encoded;
}

// Numbering comes from the shared route numbering so this node and the
// sidebar row name the same waypoint the same way (UI-02).
function waypointName(waypoint, numbering) {
  const authored = typeof waypoint.name === 'string' && waypoint.name.trim()
    ? waypoint.name.trim()
    : (typeof waypoint.label === 'string' ? waypoint.label.trim() : '');
  // A branch waypoint says so: its number alone ("2·B1") would leave a
  // screen-reader user to decode the notation (ROUTE-01c). It is named as
  // the list and the chip name it, the branch after (UI-06 J-02).
  const base = numbering.branchId
    ? `Waypoint ${numbering.displayNumber} (branch ${numbering.branchLetter})`
    : `${numbering.isMajor ? 'Major waypoint' : 'Minor waypoint'} ${numbering.displayNumber}`;
  return authored ? `${base} — ${authored}` : base;
}

/**
 * A waypoint's name as running text says it, after "Select" or "After": the
 * kind in lower case, mid-sentence (UI-06 B-15). An authored name, and a
 * branch waypoint's "Waypoint 1-B1 (branch B)", are names and keep their
 * capitals; the entry's heading, which starts with the name, keeps `name`.
 * @param {string} name - `waypointName`'s
 * @param {Object} numbering
 * @returns {string}
 */
function runningName(name, numbering) {
  if (numbering.branchId) return name;
  return name.replace(/^(Major|Minor) waypoint\b/, kind => kind.toLowerCase());
}

function nodeName(node, index) {
  const authored = typeof node.label === 'string' ? node.label.trim() : '';
  const type = node.type === 'normal' ? 'pass-through' : node.type;
  const base = `Node ${index + 1} — ${type}`;
  return authored ? `${base} — ${authored}` : base;
}

function emitterDescriptor(emitter, index, layer, groupContexts, waitingContexts, waitingReason) {
  const layerId = layer.id;
  return {
    key: sceneOutlineKey('emitter', layerId, emitter.id),
    id: emitter.id,
    index,
    primary: index === 0,
    dotCount: emitter.dotCount,
    speed: emitter.speed,
    speedVariance: percent(emitter.speedVariance),
    speedVarianceCanonical: emitter.speedVariance,
    dotSize: emitter.dotSize,
    dotColor: emitter.dotColor,
    lifecycleMode: emitter.lifecycleMode,
    releaseStart: percent(emitter.releaseStart),
    releaseStartCanonical: emitter.releaseStart,
    releaseDuration: percent(emitter.releaseDuration),
    releaseDurationCanonical: emitter.releaseDuration,
    onsetVariance: percent(emitter.onsetVariance),
    onsetVarianceCanonical: emitter.onsetVariance,
    intensityRamp: percent(emitter.intensityRamp),
    intensityRampCanonical: emitter.intensityRamp,
    busynessEnvelope: emitter.busynessEnvelope.map(handle => ({
      time: percent(handle.time),
      timeCanonical: handle.time,
      value: percent(handle.value),
      valueCanonical: handle.value,
      transition: handle.transition,
    })),
    wobble: percent(emitter.wobble),
    wobbleCanonical: emitter.wobble,
    seed: emitter.seed,
    waitAtStart: emitter.waitAtStart ?? false,
    waitingReason: waitingUnavailableReason(layer, emitter, waitingReason),
    waitingActive: waitingActive(layer, emitter, waitingReason),
    waitingContext: waitingContexts?.get(
      `${sceneOutlineKey('emitter', layerId, emitter.id)}:waiting-apply`)?.context ?? null,
    groups: emitter.groups ?? false,
    groupSize: emitter.groupSize ?? CROWD_GROUPS.SIZE,
    groupWidth: shiftDecimal(emitter.groupWidth ?? CROWD_GROUPS.WIDTH, 2),
    groupWidthCanonical: emitter.groupWidth ?? CROWD_GROUPS.WIDTH,
    groupsReason: groupsUnavailableReason(layer, emitter),
    groupsActive: groupsActive(layer, emitter),
    groupsContext: groupContexts?.get(
      `${sceneOutlineKey('emitter', layer.id, emitter.id)}:groups-apply`)?.context ?? null,
  };
}

function graphDescriptor(layer, hasBackgroundImage, nodeAreaDescriptions, nodeEntryContexts) {
  const nodes = layer.graph.getNodes();
  const entryPercents = new Map(getGraphEntryShares(layer.graph).map(row => [row.node.id, row.percent]));
  const nodeNumber = new Map(nodes.map((node, index) => [node.id, index + 1]));
  const edges = layer.graph.getEdges().map((edge, index) => ({
    key: sceneOutlineKey('edge', layer.id, edge.id),
    id: edge.id,
    index,
    sourceId: edge.sourceId,
    targetId: edge.targetId,
    sourceName: `Node ${nodeNumber.get(edge.sourceId) ?? '?'}`,
    targetName: `Node ${nodeNumber.get(edge.targetId) ?? '?'}`,
    direction: edge.direction,
    weight: edge.weight,
    controlPoints: edge.controlPoints.map((point, controlIndex) => ({
      key: sceneOutlineKey('control', layer.id, edge.id, controlIndex),
      index: controlIndex,
      x: percent(point.x),
      y: percent(point.y),
      xLabel: percentLabel(point.x),
      yLabel: percentLabel(point.y),
      xCanonical: point.x,
      yCanonical: point.y,
    })),
  }));

  return {
    key: sceneOutlineKey('network', layer.id),
    active: layer.guideType === 'graph',
    nodes: nodes.map((node, index) => ({
      key: sceneOutlineKey('node', layer.id, node.id),
      id: node.id,
      index,
      name: nodeName(node, index),
      x: percent(node.x),
      y: percent(node.y),
      xLabel: percentLabel(node.x),
      yLabel: percentLabel(node.y),
      xCanonical: node.x,
      yCanonical: node.y,
      type: node.type,
      ...(node.type === 'entry' || node.entryWeight !== undefined ? {
        entryWeight: entryWeightValue(node.entryWeight), entryPercent: entryPercents.get(node.id) ?? null,
        entryWeightContext: nodeEntryContexts?.get(JSON.stringify([layer.id, node.id])) ?? null,
      } : {}),
      ...(['entry', 'exit'].includes(node.type) || node.area ? { area: node.area ? { ...node.area } : null,
        hasBackgroundImage, areaDescription: nodeAreaDescriptions?.get(JSON.stringify([layer.id, node.id])) } : {}),
      label: node.label ?? '',
      connectedEdges: layer.graph.getEdgesForNode(node.id).length,
    })),
    edges,
  };
}

function containsSelectionKey(route, crowds, selectionKey) {
  if (!selectionKey) return false;
  for (const waypoint of route) {
    if (selectionKey === waypoint.key) return true;
    if (waypoint.area.shape !== 'polygon') continue;
    if (selectionKey === waypoint.area.key) return true;
    if (waypoint.area.points.some(point => selectionKey === point.key)) return true;
  }
  for (const crowd of crowds) {
    if (selectionKey === crowd.key || selectionKey === crowd.graph.key) return true;
    if (crowd.emitters.some(emitter => selectionKey === emitter.key)) return true;
    if (crowd.graph.nodes.some(node => selectionKey === node.key)) return true;
    for (const edge of crowd.graph.edges) {
      if (selectionKey === edge.key) return true;
      if (edge.controlPoints.some(point => selectionKey === point.key)) return true;
    }
  }
  return false;
}

/**
 * Build the plain scene-outline view model.
 * @param {Object} options
 * @param {Array<Object>} options.waypoints
 * @param {import('../models/Scene.js').Scene} options.scene
 * @param {string|null} [options.selectionKey]
 * @param {string|null} [options.focusKey]
 */
export function buildSceneOutlineSnapshot({
  waypoints = [],
  scene,
  selectionKey = null,
  focusKey = null,
  hasBackgroundImage = false,
  nodeAreaDescriptions = null,
  nodeEntryContexts = null,
  emitterGroupContexts = null,
  emitterWaitingContexts = null,
  crowdDuplicateContexts = null,
  crowdDuplicateBusy = false,
} = {}) {
  const numbering = buildRouteNumbering(waypoints);
  const route = waypoints.map((waypoint, routeIndex) => {
    const routeNumber = numbering[routeIndex];
    const points = Array.isArray(waypoint.areaHighlight?.points)
      ? waypoint.areaHighlight.points
      : [];
    const area = waypoint.areaHighlight || {};
    const name = waypointName(waypoint, routeNumber);
    return {
      key: sceneOutlineKey('waypoint', waypoint.id),
      id: waypoint.id,
      routeIndex,
      majorNumber: routeNumber.majorNumber,
      minorNumber: routeNumber.minorNumber,
      branchId: routeNumber.branchId,
      branchLetter: routeNumber.branchLetter,
      forkNumber: routeNumber.forkNumber,
      legNumber: routeNumber.legNumber,
      displayNumber: routeNumber.displayNumber,
      isMajor: Boolean(waypoint.isMajor),
      name,
      runningName: runningName(name, routeNumber),
      x: percent(waypoint.imgX),
      y: percent(waypoint.imgY),
      xLabel: percentLabel(waypoint.imgX),
      yLabel: percentLabel(waypoint.imgY),
      xCanonical: waypoint.imgX,
      yCanonical: waypoint.imgY,
      pauseSeconds: Number(waypoint.pauseTime || 0) / 1000,
      pauseMsCanonical: Number(waypoint.pauseTime || 0),
      segmentSpeed: Number(waypoint.segmentSpeed ?? 1),
      area: {
        key: sceneOutlineKey('polygon', waypoint.id),
        enabled: Boolean(area.enabled),
        shape: area.shape || 'none',
        fadeInSeconds: Number(area.fadeInMs || 0) / 1000,
        fadeOutSeconds: Number(area.fadeOutMs || 0) / 1000,
        fadeInMsCanonical: Number(area.fadeInMs || 0),
        fadeOutMsCanonical: Number(area.fadeOutMs || 0),
        points: points.map((point, index) => ({
          key: sceneOutlineKey('vertex', waypoint.id, index),
          index,
          x: percent(point.x),
          y: percent(point.y),
          xLabel: percentLabel(point.x),
          yLabel: percentLabel(point.y),
          xCanonical: point.x,
          yCanonical: point.y,
        })),
      },
    };
  });

  const layers = scene?.getFlowLayers?.() || [];
  const crowds = layers.map((layer, index) => {
    const waitingReason = waitingLayerReason(layer);
    return ({
    key: sceneOutlineKey('crowd', layer.id),
    id: layer.id,
    index,
    name: layer.name,
    displayName: typeof layer.name === 'string' && layer.name.trim()
      ? layer.name
      : `Crowd ${index + 1}`,
    duplicateContext: crowdDuplicateContexts?.get(layer.id) ?? null,
    duplicateBusy: crowdDuplicateBusy,
    choiceRole: groupChoiceRole(layer),
    visible: layer.visible,
    guideType: layer.guideType,
    emitters: layer.getEmitters().map((emitter, emitterIndex) =>
      emitterDescriptor(emitter, emitterIndex, layer, emitterGroupContexts, emitterWaitingContexts, waitingReason)
    ),
    graph: graphDescriptor(layer, hasBackgroundImage, nodeAreaDescriptions, nodeEntryContexts),
    });
  });

  return {
    route,
    crowds,
    majorCount: route.filter(waypoint => waypoint.isMajor).length,
    minorCount: route.filter(waypoint => !waypoint.isMajor).length,
    selectionKey: containsSelectionKey(route, crowds, selectionKey) ? selectionKey : null,
    focusKey,
  };
}
